import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { AppState } from '@/shared/config/storeConfig';
import { useAppDispatch } from '@/shared/lib/hooks';
import { composerDraftStorage } from '../lib/composerDraftStorage';
import {
  addAttachments,
  clearDraft,
  removeAttachment,
  setText,
  upsertDraftFromStorage,
  type AttachmentMeta,
} from '../model/composerDraftsSlice';

const useDebouncedWrite = (effect: () => void, deps: unknown[], delayMs: number) => {
  useEffect(() => {
    const t = window.setTimeout(effect, delayMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export function useComposerDraft(draftKey: string, initialText?: string, debounceMs = 350) {
  const dispatch = useAppDispatch();

  const draft = useSelector((s: AppState) => s.composerDrafts.byKey[draftKey]);

  useEffect(() => {
    const stored = composerDraftStorage.read(draftKey);
    if (stored) {
      dispatch(
        upsertDraftFromStorage({ key: draftKey, text: stored.text, updatedAt: stored.updatedAt }),
      );
    } else if (initialText) {
      // Если нет сохраненного draft, используем initialText
      dispatch(setText({ key: draftKey, text: initialText }));
    }
  }, [dispatch, draftKey, initialText]);

  const text = draft?.text ?? '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attachments = draft?.attachments ?? [];
  const updatedAt = draft?.updatedAt ?? 0;

  useDebouncedWrite(
    () => {
      composerDraftStorage.write(draftKey, { text, updatedAt: Date.now() });
    },
    [draftKey, text, updatedAt],
    debounceMs,
  );

  return useMemo(
    () => ({
      text,
      attachments,

      setText: (next: string) => dispatch(setText({ key: draftKey, text: next })),
      addAttachments: (items: AttachmentMeta[]) =>
        dispatch(addAttachments({ key: draftKey, items })),
      removeAttachment: (id: string) => dispatch(removeAttachment({ key: draftKey, id })),

      clear: () => {
        composerDraftStorage.clear(draftKey);
        dispatch(clearDraft({ key: draftKey }));
      },
    }),
    [dispatch, draftKey, text, attachments],
  );
}
